import { ROADMAP_REQUEST_FIELDS, SPECIAL_ACTION_IDS } from '@/roadmaps/constants/roadmaps.constants';
import * as api from '@/vuex/modules/roadmaps/api/roadmaps.api';
import { asyncQueue } from '@/vuex/modules/roadmaps/async-queue/async-queue';
import filter from 'lodash/filter';
import keyBy from 'lodash/keyBy';
import mapValues from 'lodash/mapValues';
import pick from 'lodash/pick';
import Vue from 'vue';

const state = {
    columns: {},
};

const getters = {
    columns: (state) => {
        return {
            ...state.columns,
            ...asyncQueue.queuedUpdates.columns,
        };
    },
    orderedColumns: (_state, getters) => {
        return Object.values(getters.columns).sort((a, b) => a.index - b.index);
    },
    columnById: (_state, getters) => (columnId) => getters.columns[columnId],
    columnIds: (_state, getters) => Object.keys(getters.columns),
    columnCount: (_state, getters) => Object.keys(getters.columns).length,
};

const mutations = {
    setMap (state, { columns }) {
        Vue.set(
            state,
            'columns',
            mapValues(columns, (column) => Object.freeze(column)),
        );
    },
    updateMapAtId (state, { id, column }) {
        const frozenItem = Object.freeze(Object.assign({}, state.columns[id], column));
        Vue.set(state.columns, id, frozenItem);
    },
};

const actions = {
    async updateColumn ({ commit, rootGetters }, columnToUpdate) {
        const task = async () => {
            const currentRoadmap = rootGetters['roadmaps/roadmaps/currentRoadmap'];
            const payload = {
                column: pick(columnToUpdate, ROADMAP_REQUEST_FIELDS.column),
                roadmapVersionTimestamp: currentRoadmap.lastUpdatedAt,
            };

            const column = await api.updateColumn(currentRoadmap.id, payload);

            commit('updateMapAtId', { id: column.id, column });

            return column;
        };

        return asyncQueue.enqueue({
            task,
            updates: keyBy([columnToUpdate], 'id'),
            updateType: 'columns',
        });
    },

    async createColumn ({ commit, getters, rootGetters }, { columnToCreate, index }) {
        const task = async () => {
            const currentRoadmap = rootGetters['roadmaps/roadmaps/currentRoadmap'];
            const payload = {
                column: pick(columnToCreate, ROADMAP_REQUEST_FIELDS.column),
                index,
                roadmapVersionTimestamp: currentRoadmap.lastUpdatedAt,
            };

            const columns = await api.createColumn(currentRoadmap.id, payload);

            commit('setMap', { columns });

            return columns;
        };

        return asyncQueue.enqueue({
            task,
            updates: keyBy(
                [
                    {
                        id: SPECIAL_ACTION_IDS.CREATE_COLUMN,
                        index,
                        ...columnToCreate,
                    },
                    ...Object.values(getters.columns)
                        .slice(index)
                        .map((column) => {
                            return {
                                ...column,
                                index: column.index + 1,
                            };
                        }),
                ],
                'id',
            ),
            updateType: 'columns',
        });
    },

    async deleteColumn ({ getters, dispatch, rootGetters }, columnToDeleteId) {
        const task = async () => {
            const currentRoadmap = rootGetters['roadmaps/roadmaps/currentRoadmap'];
            const payload = {
                roadmapVersionTimestamp: currentRoadmap.lastUpdatedAt,
            };
            const updatedRoadmap = await api.deleteColumn(currentRoadmap.id, columnToDeleteId, payload);

            dispatch('roadmaps/roadmaps/setOne', updatedRoadmap, { root: true });

            return updatedRoadmap;
        };

        const columnsWithoutDeleted = filter(getters.columns, (column) => column.id !== columnToDeleteId);

        return asyncQueue.enqueue({
            task,
            updates: keyBy(columnsWithoutDeleted, 'id'),
            updateType: 'columns',
        });
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
